"use client";

import { trackGaEvent } from "@/lib/gtag";
import { capture } from "@/lib/analytics";
import type { WorkspaceIntent } from "@/lib/workspaceRouting";
import {
  marketingCampaignSource,
  persistSignupAttribution,
  readSignupAttribution,
  signupAttributionCaptureProps,
} from "@/lib/signupAttribution";

/**
 * "Continue with Google" — a plain full-page navigation to /api/auth/google
 * (an OAuth redirect, so not a fetch). Fires the signup_started funnel event
 * with method=google before navigating; gtag uses sendBeacon so it survives
 * the navigation. Only rendered when Google is configured server-side.
 */
export function GoogleAuthButton({ label = "Continue with Google", workspaceIntent, next, campaignSource }: { label?: string; workspaceIntent?: WorkspaceIntent; next?: string | null; campaignSource?: string }) {
  const campaign = marketingCampaignSource(campaignSource);
  return (
    <a
      href={`/api/auth/google?${new URLSearchParams({ ...(workspaceIntent ? { intent: workspaceIntent } : {}), ...(next ? { next } : {}), ...(campaign ? { src: campaign } : {}) })}`}
      onClick={(event) => {
        const attribution = signupAttributionCaptureProps();
        if (workspaceIntent) attribution.intent = workspaceIntent;
        else delete attribution.intent;
        persistSignupAttribution({ ...readSignupAttribution(), ...(campaign ? { source: campaign } : {}), ...(workspaceIntent ? { intent: workspaceIntent } : {}) });
        const saved = readSignupAttribution();
        const intent = workspaceIntent;
        event.currentTarget.href = `/api/auth/google?${new URLSearchParams({ ...(intent ? { intent } : {}), ...(next ? { next } : {}), ...(saved.landingAudience ? { audience: saved.landingAudience } : {}), src: (saved.source || "google").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 100) })}`;
        trackGaEvent("signup_started", { method: "google", ...attribution });
        capture("signup_started", { method: "google", ...attribution });
      }}
      className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-border bg-background px-3 py-2.5 text-sm font-medium hover:bg-muted transition-colors"
    >
      <svg className="h-4 w-4" viewBox="0 0 18 18" aria-hidden="true">
        <path
          fill="#4285F4"
          d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        />
        <path
          fill="#FBBC05"
          d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.167 6.656 3.58 9 3.58z"
        />
      </svg>
      {label}
    </a>
  );
}
